import { Button, ClickAwayListener, createStyles, makeStyles, Popper } from '@material-ui/core';
import yellow from '@material-ui/core/colors/yellow.js';
import { Delete } from '@material-ui/icons';
import React, { useEffect, useRef, useState } from 'react';
import { useLocalization } from "../../index.js";
import { useDocumentPermissions, useViewerState } from "../../hooks/index.js";
const useStyles = makeStyles(theme => createStyles({
  root: {
    position: 'absolute',
    resize: ({
      permissions,
      rotationDegree
    }) => permissions.canEdit && rotationDegree === 0 ? 'both' : 'none',
    overflow: 'auto',
    backgroundColor: yellow[600],
    opacity: 0.5
  },
  button: {
    backgroundColor: theme.palette.error.light
  }
}));

/**
 * Return a styled highlight component
 * @param shape Shape attributes
 * @param permissions Permissions of the current user
 * @param dimensions Shape dimensions
 * @param zoomRatio Zoom ratio
 * @param onDragStart Function triggered on drag event
 * @param onResized Function triggered on resized event
 * @param removeShape Function triggered on delete
 * @returns styled highlight component
 */

export const ShapeHighlight = props => {
  const permissions = useDocumentPermissions();
  const classes = useStyles({
    ...props,
    permissions
  });
  const localization = useLocalization();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const id = open ? 'highlight-delete' : undefined;
  const highlightElement = useRef(null);
  const viewerState = useViewerState();
  const {
    updateState
  } = viewerState;
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (viewerState.currentlyResizedElementId === props.shape.guid) {
        var _highlightElement$cur;
        updateState({
          currentlyResizedElementId: undefined
        });
        const newSize = props.onResized((_highlightElement$cur = highlightElement.current) === null || _highlightElement$cur === void 0 ? void 0 : _highlightElement$cur.getClientRects()[0]);
        if (highlightElement.current && newSize) {
          highlightElement.current.style.width = `${newSize.w * props.zoomRatio}px`;
          highlightElement.current.style.height = `${newSize.h * props.zoomRatio}px`;
        }
      }
    };
    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [props, updateState, viewerState.currentlyResizedElementId]);
  useEffect(() => {
    const mutationObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'style') {
          updateState({
            currentlyResizedElementId: props.shape.guid
          });
        }
      });
    });
    highlightElement.current && mutationObserver.observe(highlightElement.current, {
      attributes: true
    });
    return () => mutationObserver.disconnect();
  }, [props.shape.guid, updateState]);
  const onRightClick = event => {
    event.preventDefault();
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.root,
    tabIndex: 0,
    draggable: permissions.canEdit,
    onDragStart: props.onDragStart,
    onContextMenu: onRightClick,
    ref: highlightElement,
    style: {
      top: props.dimensions.top,
      left: props.dimensions.left,
      width: `${props.dimensions.width}px`,
      height: `${props.dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement(Popper, {
    id: id,
    open: open,
    anchorEl: anchorEl,
    placement: "right-start"
  }, /*#__PURE__*/React.createElement(ClickAwayListener, {
    onClickAway: () => setAnchorEl(null)
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    className: classes.button,
    onMouseUp: () => props.removeShape('highlights', props.shape.guid),
    startIcon: /*#__PURE__*/React.createElement(Delete, {
      scale: props.zoomRatio
    })
  }, localization.delete))));
};
//# sourceMappingURL=shape-highlight.js.map